import React, { useEffect, useState, useCallback } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { toast, ToastContainer } from 'react-toastify';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import { useNavigate, useLocation } from 'react-router-dom';
import QRCode from 'qrcode';
import 'react-toastify/dist/ReactToastify.css';
import 'react-loading-skeleton/dist/skeleton.css';
import TooltipTextPopupCustom from './child/TooltipTextPopupCustom';


const TableDataLayerDispatch = () => {
    const [complaints, setComplaints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [qrCodes, setQrCodes] = useState({});
    const [qrDataUrl, setQrDataUrl] = useState({});
    const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
    const navigate = useNavigate();
    const location = useLocation();
      const APP_BASE_URL = process.env.REACT_APP_URL;

    const fetchComplaints = useCallback(async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_BASE_URL}/backend/complaints/get-non-dispatch-complaints`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (response.data.success) {
                setComplaints(response.data.data);
                generateQrCodes(response.data.data,APP_BASE_URL);
            } else {
                toast.error("Failed to fetch complaints.");
            }
        } catch (error) {
            console.error("Error fetching complaints:", error);
            toast.error("Error fetching complaints.");
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL,APP_BASE_URL]);

    const generateQrCodes = async (complaintsData,APP_BASE_URL) => {
        const newQrCodes = {};
        for (const complaint of complaintsData) {
            try {
               
                const qrCodeData = `${APP_BASE_URL}/complains/view/${complaint._id}`;
                const qrCodeUrl = await QRCode.toDataURL(JSON.stringify(qrCodeData));
                setQrDataUrl(qrCodeData);
                newQrCodes[complaint._id] = qrCodeUrl;
            } catch (error) {
                console.error("Error generating QR code:", error);
                newQrCodes[complaint._id] = null;
            }
        }
        setQrCodes(newQrCodes);
    };

    useEffect(() => {
        fetchComplaints();
    }, [fetchComplaints]);

    useEffect(() => {
        if (location.state?.showToast) {
            toast.success("Complaint Dispatched Successfully!");
        }
    }, [location.state]);

    const getPriorityClasses = (priority) => {
        switch (priority) {
            case "Low":
                return {
                    bg: 'bg-gradient-start-3',
                    border: 'border-info-300',
                    text: 'text-info-600',
                    glow: 'info-glow',
                    pulse: 'info-pulse',
                    wave: 'from-info-400 to-info-100',
                    ripple: 'info-100',
                    badge: 'bg-info-focus text-info-600',
                    gradient: 'bg-dark-info-gradient',
                    tooltip: 'tooltip-info'
                };
            case "Medium":
                return {
                    bg: 'bg-gradient-start-4',
                    border: 'border-success-300',
                    text: 'text-success-600',
                    glow: 'success-glow',
                    pulse: 'success-pulse',
                    wave: 'from-success-400/20 to-success-100/20',
                    ripple: 'success-100',
                    badge: 'bg-success-focus text-success-600',
                    gradient: 'bg-dark-success-gradient',
                    tooltip: 'tooltip-success'
                };
            case "High":
                return {
                    bg: 'bg-gradient-start-9',
                    border: 'border-warning-300/30',
                    text: 'text-warning-600',
                    glow: 'warning-glow',
                    pulse: 'warning-pulse',
                    wave: 'from-warning-400/20 to-warning-100/20',
                    ripple: 'warning-100',
                    badge: 'bg-warning-focus text-warning-600',
                    gradient: 'bg-dark-warning-gradient',
                    tooltip: 'tooltip-warning'
                };
            case "Critical":
                return {
                    bg: 'bg-gradient-start-5',
                    border: 'border-danger-300',
                    text: 'text-danger-600',
                    glow: 'danger-glow',
                    pulse: 'danger-pulse',
                    wave: 'from-danger-400 to-danger-100',
                    ripple: 'danger-100',
                    badge: 'bg-danger-focus text-danger-600',
                    gradient: 'bg-dark-danger-gradient',
                    tooltip: 'tooltip-danger'
                };
            default:
                return {
                    bg: 'bg-gradient-start-1',
                    border: 'border-secondary-300/30',
                    text: 'text-secondary-600',
                    glow: 'secondary-glow',
                    pulse: 'secondary-pulse',
                    wave: 'from-secondary-400/20 to-secondary-100/20',
                    ripple: 'secondary-100',
                    badge: 'bg-secondary text-secondary-600',
                    gradient: 'bg-dark-secondary-gradient'
                };
        }
    };

    const getTimeElapsed = (dateString) => {
        if (!dateString) return 'N/A';
        const now = new Date();
        const created = new Date(dateString);
        const diff = now - created;

        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) return `${days}d ${hours % 24}h ago`;
        if (hours > 0) return `${hours}h ${minutes % 60}m ago`;
        if (minutes > 0) return `${minutes}m ago`;
        return 'Just now';
    };

    const handleEditComplain = (complaintId, e) => {
        if (e) {
            const button = e.currentTarget;
            const ripple = document.createElement('span');
            const rect = button.getBoundingClientRect();
            const size = Math.max(rect.width, rect.height);
            const x = e.clientX - rect.left - size / 2;
            const y = e.clientY - rect.top - size / 2;

            ripple.style.width = ripple.style.height = `${size}px`;
            ripple.style.left = `${x}px`;
            ripple.style.top = `${y}px`;
            ripple.classList.add('ripple-effect');

            const priority = button.closest('.complaint-card').dataset.priority;
            const rippleColor = getPriorityClasses(priority).ripple;
            ripple.style.backgroundColor = `var(--${rippleColor})`;

            button.appendChild(ripple);

            setTimeout(() => {
                ripple.remove();
            }, 600);
        }

        navigate(`/dispatch/update/${complaintId}`);
    };

    return (
        <div className="basic-data-table ">
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="card-header bg-transparent border-bottom-0 pb-0 ">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-1">
                            Open Complaints
                        </h2>
                        <p className="text-sm text-gray-500">
                            Needs to be moved to In Progress (Dispatch the Complaint)
                        </p>
                    </div>
                    <span className={`px-4 py-2 rounded-full text-xs font-semibold bg-warning-100/20 text-warning-800 border border-warning-300/30 warning-glow`}>
                        {complaints.length} Open
                    </span>
                </div>
            </div>
            <div className="card-body pt-0">
                <div className="row">
                    {loading ? (
                        Array.from({ length: 6 }).map((_, index) => (
                            <div 
                            key={index} 
                            className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-6"
                            >
                              
                                  <div className="card h-full overflow-hidden border-0 rounded-xl shadow-xs hover:shadow-md transition-all duration-300">
                                    {/* Priority Top Border (you can use a thin red bar for critical) */}
                                    <div className="h-1 w-full bg-red-500" />

                                    <div className="card-body p-5">
                                        <div className="flex flex-col md:flex-row justify-between items-start w-full gap-4">
                                        {/* Left Section */}
                                        {[...Array(7)].map((_, idx) => (
                                            <div className="flex-1 w-full space-y-3">
                                                {/* Complaint ID and Badge */}
                                                <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
                                                    <Skeleton height={24} width="160px" className="rounded" />
                                                    <Skeleton height={24} width="80px" className="rounded-full" />
                                                </div>

                                                
                                                <Skeleton height={20} width="200px" className="rounded" />
                                                <Skeleton height={16} width="140px" className="rounded" />
                                            </div>

                                        ))}
                                        
                                      
                                        </div>

                                    </div>
                                    </div>

                        
                            </div>
                        ))
                        ) : complaints.length > 0 ? (
                        complaints.map((complaint) => {
                            const priority = getPriorityClasses(complaint.priority_level);
                            const timeElapsed = getTimeElapsed(complaint.time_of_call);
                            const qrCodeUrl = qrCodes[complaint._id];
                            return (
                               <div key={complaint._id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-6">
                                    <div 
                                        className={`complaint-card card h-full overflow-hidden border ${priority.bg} ${priority.border} rounded-xl bg-white/80 backdrop-blur-sm relative ${priority.glow} ${priority.pulse}`}
                                        onClick={(e) => handleEditComplain(complaint._id, e)}
                                        data-priority={complaint.priority_level}
                                    >
                                        {/* Priority indicator top border */}
                                        <div className={`absolute top-0 left-0 w-full h-1 ${priority.gradient}`}></div>
                                           

                                        <div className="card-body p-5">
                                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start w-100">
                                                {/* Left Content - takes remaining space */}
                                                <div className="flex-grow-1 me-md-3 mb-3 mb-md-0">
                                                    {/* Complaint Number and Status */}
                                                    <div className="d-flex flex-wrap align-items-center mb-2">
                                                        <h3 className="text-lg font-bold text-gray-900 mb-0 me-3" style={{whiteSpace: 'nowrap'}}>
                                                            #{complaint.complaint_number || 'N/A'}
                                                        </h3>
                                                        <div className="d-flex flex-column">
                                                            <span className={`badge text-xs font-semibold ${priority.gradient} px-3 py-1 rounded-full text-white me-2 mb-1`}>
                                                                {complaint.priority_level || 'N/A'}
                                                            </span>
                                                            <span className={`text-xs font-semibold ${priority.text} d-flex align-items-center`}>
                                                                <Icon icon="mdi:clock-outline" className="me-1" style={{ lineHeight: 1 }} />
                                                                Elapsed from time of call {timeElapsed}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Department Info with added space */}
                                                    <table className="w-100 mb-3"> {/* Added mb-3 for space after department */}
                                                        <tbody>
                                                            <tr className="border-top">
                                                                <td className="py-2 text-muted fw-medium w-25" style={{ whiteSpace: 'nowrap' }}>
                                                                    <Icon icon="bi:building" className="me-2 text-muted align-middle" /> 
                                                                    <span className="align-middle">Department:</span>
                                                                </td>
                                                                <td className="py-2">
                                                                    <span className="text-body">{complaint.dept_id?.name || 'N/A'}</span>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>

                                                {/* Right Content - QR Code (keeps its original width) */}
                                                <div className="flex-shrink-0">
                                                     {qrCodeUrl && (
                                                <div className="flex-shrink-0">
                                                <a
                                                    href={qrDataUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-decoration-none text-reset" style={{zIndex: 9}}
                                                >
                                                    <div
                                                    className="qr-code-container"
                                                    style={{
                                                        width: '90px',
                                                        height: '90px',
                                                        background: 'white',
                                                        padding: '4px',
                                                        borderRadius: '6px',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                                        cursor: 'pointer'
                                                    }}
                                                    >
                                                    <img
                                                        src={qrCodeUrl}
                                                        alt="Complaint QR Code"
                                                        style={{ 
                                                        width: '100%', 
                                                        height: '100%', 
                                                        objectFit: 'contain'
                                                        }}
                                                        onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = '';
                                                        e.target.parentElement.style.display = 'flex';
                                                        e.target.parentElement.innerHTML = `
                                                            <div style="text-align: center; padding: 10px;">
                                                            <div style="font-size: 14px; margin-bottom: 8px;">QR Code Error</div>
                                                            <div style="font-size: 12px; color: #666;">Click to view complaint</div>
                                                            </div>`;
                                                        }}
                                                    />
                                                    </div>
                                                </a>
                                                </div>
                                            )}
                                                </div>
                                            </div>
                                           
                                            {/* Complaint details in table format */}
                                           <div className="mt-4 border-top pt-3" >
                                                <table className="table table-responsive scroll-sm">
                                                 <tbody>
                                                    <tr className="border-bottom">
                                                        <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                            <Icon icon="bi:geo-alt" className="me-2 text-muted align-middle" /> 
                                                            <span className="align-middle">District</span>
                                                        </td>
                                                        <td className={`py-2 ${priority.bg}`}>
                                                            <TooltipTextPopupCustom 
                                                                text={complaint.complainant_district_id?.name || complaint.district_id?.name || complaint.complaint_district_id?.name || 'N/A'} 
                                                                truncateLength={20} 
                                                                placement="right" 
                                                                customClass={`${priority.tooltip}`} 
                                                            />
                                                        </td>
                                                    </tr>
                                                    <tr className="border-bottom">
                                                        <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                            <Icon icon="bi:pin-map" className="me-2 text-muted align-middle" /> 
                                                            <span className="align-middle">Tehsil</span>
                                                        </td>
                                                        <td className={`py-2 ${priority.bg} text-body`}>
                                                            <TooltipTextPopupCustom 
                                                                text={complaint.complaint_tehsil_id?.name || 'N/A'} 
                                                                truncateLength={20} 
                                                                placement="right" 
                                                                customClass={`${priority.tooltip}`} 
                                                            />
                                                        </td>
                                                    </tr>
                                                    <tr className="border-bottom">
                                                        <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                            <Icon icon="bi:calendar" className="me-2 text-muted align-middle" /> 
                                                            <span className="align-middle">Referred By</span>
                                                        </td>
                                                        <td className={`py-2 ${priority.bg} text-body`}>
                                                            <TooltipTextPopupCustom 
                                                                text={complaint.complaint_reffered_by || 'N/A'} 
                                                                truncateLength={20} 
                                                                placement="right" 
                                                                customClass={`${priority.tooltip}`} 
                                                            />
                                                        </td>
                                                    </tr>
                                                    <tr className="border-bottom">
                                                        <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                            <Icon icon="bi:clock" className="me-2 text-muted align-middle" /> 
                                                            <span className="align-middle">Date of Call</span>
                                                        </td>
                                                        <td className={`py-2 ${priority.bg} text-body`}>
                                                            <TooltipTextPopupCustom 
                                                                text={complaint.time_of_call ? new Date(complaint.time_of_call).toLocaleString() : 'N/A'} 
                                                                truncateLength={18} 
                                                                placement="right" 
                                                                customClass={`${priority.tooltip}`} 
                                                            />
                                                        </td>
                                                    </tr>
                                                    <tr className="border-bottom">
                                                        <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                            <Icon icon="bi:file-earmark-text" className="me-2 text-muted align-middle" /> 
                                                            <span className="align-middle">Complaint Nature</span>
                                                        </td>
                                                        <td className={`py-2 ${priority.bg} text-body`}>
                                                            <TooltipTextPopupCustom text={complaint.complaint_nature_id ? complaint.complaint_nature_id?.name : 'N/A'} truncateLength={20} placement="right" customClass={`${priority.tooltip}`} />
                                                        </td>
                                                    </tr>
                                                    <tr className="border-bottom">
                                                        <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                            <Icon icon="bi:inbox" className="me-2 text-muted align-middle" /> 
                                                            <span className="align-middle">Source</span>
                                                        </td>
                                                        <td className={`py-2 ${priority.bg} text-body`}>
                                                            <TooltipTextPopupCustom 
                                                                text={complaint.complaint_source || 'N/A'} 
                                                                truncateLength={20} 
                                                                placement="right" 
                                                                customClass={`${priority.tooltip}`} 
                                                            />
                                                        </td>
                                                    </tr>
                                                    <tr className="border-bottom">
                                                        <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                            <Icon icon="bi:person-badge" className="me-2 text-muted align-middle" /> 
                                                            <span className="align-middle">Received By</span>
                                                        </td>
                                                        <td className={`py-2 ${priority.bg} text-body`}>
                                                            <TooltipTextPopupCustom 
                                                                text={complaint.received_by ? `${complaint.received_by.first_name} ${complaint.received_by.last_name} (${complaint.received_by.designation})` : "N/A"} 
                                                                truncateLength={15} 
                                                                placement="right" 
                                                                customClass={`${priority.tooltip}`} 
                                                            />
                                                        </td>
                                                    </tr>
                                                    <tr className="border-bottom">
                                                        <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                            <Icon icon="bi:geo-alt" className="me-2 text-muted align-middle" /> 
                                                            <span className="align-middle">Nearby Place</span>
                                                        </td>
                                                        <td className={`py-2 ${priority.bg} text-body`}>
                                                            <TooltipTextPopupCustom text={complaint.near_by_place || complaint.near_by_place} truncateLength={20} placement="right" customClass={`${priority.tooltip}`} />
                                                        </td>
                                                        
                                                    </tr>
                                                    <tr className="border-bottom">
                                                        <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                            <Icon icon="bi:house" className="me-2 text-muted align-middle" /> 
                                                            <span className="align-middle">Address</span>
                                                        </td>
                                                        <td className={`py-2 ${priority.bg} text-body`}>
                                                            <TooltipTextPopupCustom text={complaint.complaint_address || complaint.complaint_address} truncateLength={20} placement="right" customClass={`${priority.tooltip}`} />
                                                        </td>
                                                    </tr>
                                                    <tr className="border-bottom">
                                                        <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                            <Icon icon="bi:chat-square-text" className="me-2 text-muted align-middle" /> 
                                                            <span className="align-middle">Description</span>
                                                        </td>
                                                        <td className={`py-2 ${priority.bg} text-body`}>
                                                            <TooltipTextPopupCustom text={complaint.description || complaint.complaint_detail} truncateLength={20} placement="right" customClass={`${priority.tooltip}`} />
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                            <Icon icon="bi:sticky" className="me-2 text-muted align-middle" /> 
                                                            <span className="align-middle">Remarks</span>
                                                        </td>
                                                        <td className={`py-2 ${priority.bg} text-body`}>
                                                            <TooltipTextPopupCustom text={complaint.remarks} truncateLength={20} placement="right" customClass={`${priority.tooltip}`} />
                                                        </td>
                                                    </tr>
                                                </tbody>
                                                </table>
                                                </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="col-12 text-center py-5 text-gray-400">You are all done! No open complaints to dispatch.</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TableDataLayerDispatch;